import { resourceAdapt } from '../adaptors/resourceAdapt';
import ServiceManager from '../../services/ServiceManager';

/**
 * Interface for the i18next services object passed to init
 */
interface I18nServices {
    resourceStore?: {
        options?: {
            ns?: string[];
        };
    };
    [key: string]: any;
}

/**
 * Interface for the custom backend options
 */
interface BackendOptions {
    resourceName: string;
    SaveManager?: new (namespace: string) => ISaveManager;
}

/**
 * Interface for the SessionStoreManager instance
 */
interface ISaveManager {
    save: (data: Record<string, any>) => void;
    load: () => Record<string, any>;
}

class Backend {
    static type = 'backend' as const;

    private resourceName: string | null = null;

    private lang: Record<string, any> = {};

    private saveManager: Partial<ISaveManager> = {};

    private resourceAdapt = resourceAdapt;

    constructor() {
        // Properties initialized via init or as class fields
    }

    init(services: I18nServices, backendOptions: BackendOptions): void {
        this.resourceName = backendOptions.resourceName;

        if (typeof backendOptions.SaveManager === 'function') {
            const nameSpace = services.resourceStore?.options?.ns?.[0] ?? 'translation';

            this.saveManager = new backendOptions.SaveManager(nameSpace);
        }
    }

    read(language: string, namespace: string, callback: (err: Error | null, data: any) => void): void {
        this.lang[language] = {};
        const localResource = this.resourcesFromSessionStorage?.[language] ?? {};

        if (Object.keys(localResource).length !== 0) {
            callback(null, localResource);
        } else {
            this.loadResource(language, namespace, callback);
        }
    }

    private saveToSessionStorage(resources: Record<string, any>): void {
        if (this.saveManager?.save) {
            this.saveManager.save({
                ...this.resourcesFromSessionStorage,
                ...resources,
            });
        }
    }

    private get resourcesFromSessionStorage(): Record<string, any> {
        if (this.saveManager?.load) {
            return this.saveManager.load();
        }

        return {};
    }

    private loadResource(
        language: string,
        namespace: string,
        callback: (err: any, data: any) => void,
    ): void {
        if (!this.resourceName) {
            callback(new Error('Resource name not initialized'), false);

            return;
        }

        ServiceManager.commonContent(
            'getResourceSetsByName',
            [
                this.resourceName,
                language,
            ],
        )
            .then(({ data }: any) => {
                const adaptedData = this.resourceAdapt(data?.values);

                this.lang[language] = adaptedData;

                callback(null, adaptedData);
                this.saveToSessionStorage({ [language]: adaptedData });
            })
            .catch((e: Error) => {
                callback(e, false);
            });
    }
}

export default Backend;
